import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import P2 components (lazy-loaded for performance)
const P2ProductList = React.lazy(() => import("./components/P2ProductList"));
const P2SettingsPage = React.lazy(() => import("./components/P2SettingsPage"));
const P2SettingsEdit = React.lazy(() => import("./components/P2SettingsEdit"));
const P2ProductForm = React.lazy(() => import("./components/P2ProductForm"));
const P2ProductView = React.lazy(() => import("./components/P2ProductView"));
const P2Cart = React.lazy(() => import("./components/P2Cart"));
const P2Checkout = React.lazy(() => import("./components/P2Checkout"));
const P2OrderDetails = React.lazy(() => import("./components/P2OrderDetails"));
const P2MyOrders = React.lazy(() => import("./components/P2MyOrders"));
const P2MyPledges = React.lazy(() => import("./components/P2MyPledges"));
const P2SavedCards = React.lazy(() => import("./components/P2SavedCards"));
const P2CampaignWizard = React.lazy(() => import("./components/P2CampaignWizard"));
const P2CampaignDashboard = React.lazy(() => import("./components/P2CampaignDashboard"));
const P2CampaignView = React.lazy(() => import("./components/P2CampaignView"));
const P2CampaignMessages = React.lazy(() => import("./components/P2CampaignMessages"));
const P2CampaignDelivery = React.lazy(() => import("./components/P2CampaignDelivery"));
const P2CampaignCharges = React.lazy(() => import("./components/P2CampaignCharges"));
const P2CampaignSuppliers = React.lazy(() => import("./components/P2CampaignSuppliers"));
const P2CampaignRefunds = React.lazy(() => import("./components/P2CampaignRefunds"));
const P2DailyChaseSettings = React.lazy(() => import("./components/P2DailyChaseSettings"));
const P2NotificationSettings = React.lazy(() => import("./components/P2NotificationSettings"));
const P2PublicCampaignPage = React.lazy(() => import("./components/P2PublicCampaignPage"));
const P2Invites = React.lazy(() => import("./components/P2Invites"));

/**
 * P2 Marketplace Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (p2/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "edit", "admin")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/p2/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const p2ModuleConfig: ModuleConfig = {
    // Module Identity
    id: "p2",
    name: "Marketplace",
    description: "Community marketplace with pledge campaigns, group buying, and Stripe payments",
    version: "1.0.0",
    enabled: true,
    category: "ecommerce",
    order: 5,
    icon: "🛒",
    color: "#38A169",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/p2",
            component: P2ProductList,
            public: true,
            useLayout: true,
        },
        {
            path: "/p2/settings",
            component: P2SettingsPage,
            permissionLevel: "admin",
            useLayout: true,
        },
        {
            path: "/p2/settings/edit",
            component: P2SettingsEdit,
            permissionLevel: "admin",
            useLayout: true,
        },
        {
            path: "/p2/new",
            component: P2ProductForm,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/product/:id",
            component: P2ProductView,
            public: true,
            useLayout: true,
        },
        {
            path: "/p2/product/:id/edit",
            component: P2ProductForm,
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/p2/cart",
            component: P2Cart,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/checkout/:orderId",
            component: P2Checkout,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/order/:id",
            component: P2OrderDetails,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/my-orders",
            component: P2MyOrders,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/my-pledges",
            component: P2MyPledges,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/my-cards",
            component: P2SavedCards,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/wizard",
            component: P2CampaignWizard,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/c/:slug",
            component: P2PublicCampaignPage,
            public: true,
            useLayout: false,
        },
        {
            path: "/p2/campaigns",
            component: P2CampaignDashboard,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/campaign/:id",
            component: P2CampaignView,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/campaign/:id/messages",
            component: P2CampaignMessages,
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/p2/campaign/:id/delivery",
            component: P2CampaignDelivery,
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/p2/campaign/:id/charges",
            component: P2CampaignCharges,
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/p2/campaign/:id/suppliers",
            component: P2CampaignSuppliers,
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/p2/campaign/:id/refunds",
            component: P2CampaignRefunds,
            permissionLevel: "admin",
            useLayout: true,
        },
        {
            path: "/p2/chase-settings",
            component: P2DailyChaseSettings,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/p2/notifications",
            component: P2NotificationSettings,
            permissionLevel: "admin",
            useLayout: true,
        },
        {
            path: "/p2/invites",
            component: P2Invites,
            permissionLevel: "admin",
            useLayout: true,
        },
    ],

    // Navigation - permissionLevel resolved from backend
    navigation: [
        {
            label: "Products",
            path: "/p2",
            icon: "\uD83D\uDCE6",
            permissionLevel: "view",
        },
        {
            label: "New Product",
            path: "/p2/new",
            icon: "\u2795",
            permissionLevel: "view",
        },
        {
            label: "Cart",
            path: "/p2/cart",
            icon: "\uD83D\uDED2",
            permissionLevel: "view",
        },
        {
            label: "My Orders",
            path: "/p2/my-orders",
            icon: "\uD83D\uDCCB",
            permissionLevel: "view",
        },
        {
            label: "My Pledges",
            path: "/p2/my-pledges",
            icon: "\uD83E\uDD1D",
            permissionLevel: "view",
        },
        {
            label: "Campaigns",
            path: "/p2/campaigns",
            icon: "\uD83C\uDFAF",
            permissionLevel: "view",
        },
        {
            label: "Wizard",
            path: "/p2/wizard",
            icon: "\u2728",
            permissionLevel: "view",
        },
        {
            label: "Settings",
            path: "/p2/settings",
            icon: "\u2699\uFE0F",
            permissionLevel: "admin",
        },
        {
            label: "Notifications",
            path: "/p2/notifications",
            icon: "\uD83D\uDD14",
            permissionLevel: "admin",
        },
        {
            label: "Invites",
            path: "/p2/invites",
            icon: "\uD83D\uDC65",
            permissionLevel: "admin",
        },
    ],

    // Empty permissions - resolved from backend modulePermissions query
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // Quick Actions
    quickActions: [
        {
            label: "Add Product",
            path: "/p2/new",
            icon: "\u2795",
            description: "Create a new marketplace product",
        },
        {
            label: "View Cart",
            path: "/p2/cart",
            icon: "\uD83D\uDED2",
            description: "View your shopping cart",
        },
        {
            label: "Launch Campaign",
            path: "/p2/wizard",
            icon: "\uD83D\uDE80",
            description: "Launch a new pledge campaign",
        },
    ],

    // Changelog
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2026-03-30",
            title: "Initial Release",
            features: [
                "Product listing and management",
                "Pledge campaigns with group buying",
                "Stripe payment integration",
                "Order management and tracking",
                "Campaign messaging system",
                "Delivery tracking",
                "Supplier management",
                "Referral system",
                "Notification rules",
                "Invite system",
            ],
        },
    ],
};

export default p2ModuleConfig;
